import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import {
  Icon,
  BaseModal,
  NoDataTitle,
  NoDataContentWrapper,
  ChartTooltipSingleMetric,
  NoDataContentWrapperProps
} from '@anytag/shared-atoms';
import { THEME } from '@anytag/shared-constants';
import { useToggle } from '@anytag/shared-hooks';
import { formatPercent } from '@anytag/shared-utils';
import { AccountAudienceDataRate } from '@anytag/graphql';
import { HighChart } from '../high-chart';
import { InfluencerAvatar } from '../influencer-avatar';

export interface BarChartWithMoreModalProps
  extends Pick<NoDataContentWrapperProps, 'header' | 'className' | 'wrapperLink' | 'wrapperText'>,
    Pick<Highcharts.ChartOptions, 'width'> {
  avatar?: string;
  hasRelativeMaxY?: boolean;
  dataset: readonly AccountAudienceDataRate[];
  title?: string;
  modalTitle?: string;
  hasCategoriesTranslate?: boolean;
}

export const BarChartWithMoreModal = ({
  width,
  header,
  avatar,
  dataset,
  title = 'Area',
  hasRelativeMaxY,
  modalTitle = 'Audience Area',
  hasCategoriesTranslate = false,
  ...noDataContentProps
}: BarChartWithMoreModalProps) => {
  const { t } = useTranslation();
  const modalState = useToggle();

  const dataNotAvailable = dataset.every(({ rate }) => rate === 0);
  const { categories, seriesData } = dataset.reduce<{ categories: string[]; seriesData: number[] }>(
    (acc, { name, rate }) => ({
      categories: [...acc.categories, hasCategoriesTranslate ? t<string>(`Category.${name}`) : name],
      seriesData: [...acc.seriesData, rate]
    }),
    { categories: [], seriesData: [] }
  );

  const chartOptions = (showAll?: boolean): Highcharts.Options => ({
    title: { text: '' },
    credits: { enabled: false },
    plotOptions: { bar: { showInLegend: false, dataLabels: { enabled: false } } },
    chart: { type: 'bar', width: showAll ? 440 : width, height: showAll ? null : 300, spacingLeft: 0 },
    xAxis: {
      title: { text: null },
      categories: showAll ? categories : categories.slice(0, 7),
      labels: {
        align: 'left',
        x: -5,
        reserveSpace: true
      }
    },
    series: [{ type: 'bar', color: '#9ea0e0', data: showAll ? seriesData : seriesData.slice(0, 7) }],
    yAxis: {
      min: 0,
      max: hasRelativeMaxY ? null : 1,
      title: { text: '' },
      labels: {
        overflow: 'justify',
        formatter() {
          return formatPercent(Number(this.value), true, 0);
        }
      }
    },
    tooltip: {
      padding: 0,
      useHTML: true,
      borderWidth: 0,
      borderRadius: 3,
      borderColor: THEME.chart.tooltip.border.colors.green,
      backgroundColor: THEME.chart.tooltip.background.colors.black,
      style: { color: THEME.chart.tooltip.text.colors.white },
      formatter() {
        return renderToStaticMarkup(
          <ChartTooltipSingleMetric
            name={t(String(this.category))}
            value={formatPercent(this.y)}
            color={this.color as string}
          />
        );
      }
    }
  });

  return (
    <>
      <NoDataContentWrapper
        className="p-4"
        dataNotAvailable={dataNotAvailable}
        header={header ?? <NoDataTitle className="mb-6" title={t(title)} />}
        {...noDataContentProps}
      >
        <HighChart options={chartOptions()} />

        <div className="flex items-center justify-start">
          <div className="cursor-pointer leading-none" onClick={modalState.toggle}>
            <span className="text-sm font-semibold text-black-60">{t('MORE')}</span>
            <Icon icon="arrow-right" variant="blue" size="11px" className="ml-1" />
          </div>
        </div>
      </NoDataContentWrapper>

      <BaseModal
        open={modalState.on}
        onClose={modalState.setOff}
        title={
          <div className="flex items-center">
            <InfluencerAvatar src={avatar} size="24px" className="rounded-full" />
            <p className="ml-2">{t(modalTitle)}</p>
          </div>
        }
        footer={{ cancelButton: { onClick: modalState.setOff } }}
      >
        <HighChart options={chartOptions(true)} />
      </BaseModal>
    </>
  );
};
